import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, RefreshCw, Send, Copy, FileText, Lock, Facebook, CheckCircle2 } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { WhatsappHistory, Card } from '@/features/whatsapp/WhatsappHistory';

interface WaTemplate {
  id: string; name: string; language: string; status: string; category: string; parameterFormat: string;
  headerText: string | null; headerFormat: string | null; bodyText: string;
  headerVars: string[]; bodyVars: string[]; buttonVars: Record<string, string[]>; buttons: Array<{ type: string; text: string; url?: string }>;
}
interface ParamMap { header?: string[]; body?: string[]; buttons?: Record<string, string[]> }
interface WaConfig {
  enabled: boolean; wabaId: string; phoneNumberId: string; apiVersion: string; verifyToken: string;
  defaultPrice: number; gstPct: number; sacCode: string; feedbackDelayHours: number;
  hasAccessToken: boolean; hasAppSecret: boolean; webhookPath: string;
  events: Array<{ event: string; label: string; B2C?: { template?: string; language?: string }; B2B?: { template?: string; language?: string } }>;
  templateParams: Record<string, ParamMap>;
  fields: Array<{ key: string; label: string }>;
  appId: string; configId: string;
  displayPhoneNumber?: string | null; verifiedName?: string | null; connectedAt?: string | null; connectedVia?: string | null;
}

const STATUS_CHIP: Record<string, string> = {
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  IN_APPEAL: 'bg-amber-50 text-amber-700 border-amber-200',
  REJECTED: 'bg-rose-50 text-rose-700 border-rose-200',
  PAUSED: 'bg-slate-100 text-slate-600 border-slate-300',
  DISABLED: 'bg-slate-100 text-slate-600 border-slate-300',
};
const key = (name?: string, lang?: string) => `${name ?? ''}|${lang ?? ''}`;
type Sub = 'connection' | 'templates' | 'events' | 'history' | 'invoices';

/**
 * Settings → WhatsApp (Meta Cloud API). The connection, the template list synced from Meta, which
 * template each event sends and what fills its placeholders, a test send, every message sent, and
 * the monthly WhatsApp invoices for postpaid merchants. Which merchants get WhatsApp, and at what
 * price, is set on each merchant (Merchant → WhatsApp).
 */
export function WhatsappSettingsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';
  const [sub, setSub] = useState<Sub>('connection');
  const { data: cfg } = useQuery({ queryKey: ['whatsapp', 'config'], queryFn: async () => (await api.get<WaConfig>('/whatsapp/config')).data });
  const { data: tpl } = useQuery({ queryKey: ['whatsapp', 'templates'], queryFn: async () => (await api.get<{ syncedAt: string | null; items: WaTemplate[] }>('/whatsapp/templates')).data });

  const tabs: Array<[Sub, string]> = [['connection', 'Connection & price'], ['templates', `Templates${tpl ? ` (${tpl.items.length})` : ''}`], ['events', 'Event → template'], ['history', 'Message history'], ['invoices', 'Postpaid invoices']];
  return (
    <div className="space-y-4" data-testid="wa-settings">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">WhatsApp messages</h2>
        <p className="text-xs text-slate-500">
          Booking, Delivered, Delivery Failed and Feedback messages to the consignee, and an Invoice notice to the merchant, over the Meta WhatsApp Cloud API —
          each with its own B2C and B2B template; B2C quotes the AWB, B2B the LR. Switch it on and set the price per merchant under <b>Merchants → (merchant) → WhatsApp</b>.
        </p>
        {cfg && (
          <div className="mt-2 text-xs">
            {cfg.enabled
              ? <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">● Sending is ON</span>
              : <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5">● Sending is OFF</span>}
            {!isAdmin && <span className="ml-2 text-slate-400 inline-flex items-center gap-1"><Lock size={11} /> view only — Super Admin / Admin change these</span>}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`wa-sub-${id}`}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${sub === id ? 'border-primary text-primary font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{label}</button>
        ))}
      </div>
      {!cfg ? <div className="py-8 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline" /></div> : (
        <>
          {sub === 'connection' && <ConnectionTab cfg={cfg} isAdmin={isAdmin} />}
          {sub === 'templates' && <TemplatesTab items={tpl?.items ?? []} syncedAt={tpl?.syncedAt ?? null} isAdmin={isAdmin} />}
          {sub === 'events' && <EventsTab cfg={cfg} items={tpl?.items ?? []} isAdmin={isAdmin} />}
          {sub === 'history' && <WhatsappHistory showMerchant />}
          {sub === 'invoices' && (isAdmin ? <InvoicesTab /> : <p className="text-sm text-slate-500">Super Admin / Admin raise the WhatsApp invoices.</p>)}
        </>
      )}
    </div>
  );
}

function ConnectionTab({ cfg, isAdmin }: { cfg: WaConfig; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...cfg, accessToken: '', appSecret: '' });
  useEffect(() => { setF({ ...cfg, accessToken: '', appSecret: '' }); }, [cfg]);
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const save = useMutation({
    mutationFn: async () => (await api.put('/whatsapp/config', {
      enabled: f.enabled, wabaId: f.wabaId, phoneNumberId: f.phoneNumberId, apiVersion: f.apiVersion, verifyToken: f.verifyToken,
      defaultPrice: f.defaultPrice, gstPct: f.gstPct, sacCode: f.sacCode, feedbackDelayHours: f.feedbackDelayHours,
      accessToken: f.accessToken, appSecret: f.appSecret, appId: f.appId, configId: f.configId,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['whatsapp', 'config'] }); toast.success('WhatsApp settings saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });
  const base = String((api.defaults as any).baseURL ?? '').replace(/\/+$/, '');
  const webhook = `${base}/whatsapp/webhook`;
  const copy = (s: string) => { navigator.clipboard?.writeText(s); toast.success('Copied'); };
  const incl = Math.round((Number(f.defaultPrice || 0) * (1 + Number(f.gstPct || 0) / 100)) * 100) / 100;
  const dis = !isAdmin;

  return (
    <div className="space-y-4 max-w-3xl">
      <EmbeddedSignup cfg={cfg} isAdmin={isAdmin} />
      {isAdmin && cfg.hasAccessToken && <FindIds cfg={cfg} />}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Meta App ID"><input disabled={dis} value={f.appId ?? ''} onChange={(e) => set('appId', e.target.value)} className="input font-mono text-xs" data-testid="wa-app-id" /></Field>
        <Field label="Login configuration ID (Facebook Login for Business)"><input disabled={dis} value={f.configId ?? ''} onChange={(e) => set('configId', e.target.value)} className="input font-mono text-xs" data-testid="wa-config-id" /></Field>
      </div>
      <div className="text-[11px] text-slate-400 -mt-2">Or enter the connection by hand:</div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="WhatsApp Business Account ID"><input disabled={dis} value={f.wabaId} onChange={(e) => set('wabaId', e.target.value)} className="input font-mono text-xs" placeholder="from Meta Business Manager" /></Field>
        <Field label="Phone Number ID"><input disabled={dis} value={f.phoneNumberId} onChange={(e) => set('phoneNumberId', e.target.value)} className="input font-mono text-xs" placeholder="WhatsApp Manager → Phone numbers" /></Field>
        <Field label={`Permanent access token ${cfg.hasAccessToken ? '(saved — hidden)' : ''}`}>
          <input disabled={dis} type="password" autoComplete="new-password" value={f.accessToken} onChange={(e) => set('accessToken', e.target.value)} className="input font-mono text-xs"
            placeholder={cfg.hasAccessToken ? '•••••••• leave blank to keep' : 'System User token with whatsapp_business_messaging'} />
        </Field>
        <Field label={`App secret ${cfg.hasAppSecret ? '(saved — hidden)' : ''}`}>
          <input disabled={dis} type="password" autoComplete="new-password" value={f.appSecret} onChange={(e) => set('appSecret', e.target.value)} className="input font-mono text-xs"
            placeholder={cfg.hasAppSecret ? '•••••••• leave blank to keep' : 'App → Settings → Basic → App secret'} />
        </Field>
        <Field label="Graph API version"><input disabled={dis} value={f.apiVersion} onChange={(e) => set('apiVersion', e.target.value)} className="input font-mono text-xs" /></Field>
        <Field label="Webhook verify token"><input disabled={dis} value={f.verifyToken} onChange={(e) => set('verifyToken', e.target.value)} className="input font-mono text-xs" placeholder="any secret word — made for you on first save" /></Field>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
        <div className="font-medium text-slate-700">Meta webhook (App → WhatsApp → Configuration)</div>
        <div className="flex items-center gap-2">Callback URL: <code className="bg-white border rounded px-1">{webhook}</code><button type="button" onClick={() => copy(webhook)} className="text-primary"><Copy size={12} /></button></div>
        <div className="flex items-center gap-2">Verify token: <code className="bg-white border rounded px-1">{cfg.verifyToken || '— save once to create —'}</code>{cfg.verifyToken && <button type="button" onClick={() => copy(cfg.verifyToken)} className="text-primary"><Copy size={12} /></button>}</div>
        <div>Subscribe to the <b>messages</b> field. Delivery / read / failed statuses come back here; a failed message is never charged.</div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <Field label="Default price / message (₹, before GST)"><input disabled={dis} type="number" step="0.01" min="0" value={f.defaultPrice} onChange={(e) => set('defaultPrice', e.target.value)} className="input" /></Field>
        <Field label="GST %"><input disabled={dis} type="number" step="0.01" min="0" value={f.gstPct} onChange={(e) => set('gstPct', e.target.value)} className="input" /></Field>
        <Field label="SAC on invoice"><input disabled={dis} value={f.sacCode} onChange={(e) => set('sacCode', e.target.value)} className="input" /></Field>
        <Field label="Feedback after delivery (hours)"><input disabled={dis} type="number" min="1" value={f.feedbackDelayHours} onChange={(e) => set('feedbackDelayHours', e.target.value)} className="input" /></Field>
      </div>
      <p className="text-[11px] text-slate-500">Default for a merchant switched on without a price of its own: ₹{Number(f.defaultPrice || 0).toFixed(2)} + {f.gstPct}% GST = <b>₹{incl.toFixed(2)}</b> per message. Prepaid merchants pay from their wallet (no balance → not sent); postpaid merchants get a monthly WhatsApp invoice.</p>
      <label className="flex items-center gap-2 text-sm">
        <input disabled={dis} type="checkbox" checked={!!f.enabled} onChange={(e) => set('enabled', e.target.checked)} data-testid="wa-enabled" />
        Send WhatsApp messages (master switch — off stops every message without losing any setting)
      </label>
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="wa-save-config"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      )}
      {isAdmin && <TestSend />}
    </div>
  );
}

/**
 * "Find my IDs": with the access token saved, list the WhatsApp Business Accounts and numbers it can
 * reach and set them with one click — no hunting for the account / number IDs in Meta's screens.
 */
function FindIds({ cfg }: { cfg: WaConfig }) {
  const qc = useQueryClient();
  const [data, setData] = useState<any>(null);
  const find = useMutation({
    mutationFn: async () => (await api.get('/whatsapp/discover')).data,
    onSuccess: (r: any) => { setData(r); if (!r.wabas?.length) toast.error('This token cannot reach any WhatsApp Business Account — check the system user has the WhatsApp account assigned.'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not read the IDs from Meta'),
  });
  const use = useMutation({
    mutationFn: async (x: { wabaId: string; phoneNumberId: string }) => {
      await api.put('/whatsapp/config', x);
      return (await api.post('/whatsapp/templates/sync')).data;
    },
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ['whatsapp'] }); toast.success(`Saved. ${r.count} template(s) synced from Meta.`); setData(null); },
    onError: (e: any) => { qc.invalidateQueries({ queryKey: ['whatsapp'] }); toast.error(e?.response?.data?.message ?? 'Saved, but the template sync failed'); },
  });
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="wa-find-ids">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] text-xs text-slate-600">
          <b className="text-slate-800">Find my IDs</b> — list the WhatsApp Business Accounts and phone numbers the saved token can reach, and use one.
        </div>
        <button onClick={() => find.mutate()} disabled={find.isPending} data-testid="wa-find-ids-btn"
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
          {find.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Find my IDs
        </button>
      </div>
      {data?.enteredId && data.enteredIdType && data.enteredIdType !== 'whatsapp_business_account' && (
        <div className="text-xs text-amber-700">The saved WhatsApp Business Account ID {data.enteredId} is a <b>{String(data.enteredIdType).replace(/_/g, ' ')}</b> — pick the right account below.</div>
      )}
      {data?.wabas?.map((w: any) => (
        <div key={w.id} className="border border-slate-100 rounded p-2 text-xs">
          <div className="font-medium text-slate-800">{w.name ?? 'WhatsApp Business Account'} · <span className="font-mono">{w.id}</span></div>
          {!w.phoneNumbers.length && <div className="text-slate-400">No phone number on this account yet.</div>}
          {w.phoneNumbers.map((p: any) => (
            <div key={p.id} className="flex items-center gap-3 mt-1">
              <span>{p.displayPhoneNumber ?? '—'} {p.verifiedName ? `(${p.verifiedName})` : ''} · <span className="font-mono">{p.id}</span>{p.qualityRating ? ` · ${p.qualityRating}` : ''}</span>
              {cfg.wabaId === w.id && cfg.phoneNumberId === p.id
                ? <span className="text-emerald-700">in use</span>
                : <button onClick={() => use.mutate({ wabaId: w.id, phoneNumberId: p.id })} disabled={use.isPending} className="px-2 py-0.5 bg-primary text-white rounded text-xs disabled:opacity-60" data-testid={`wa-use-${p.id}`}>Use this</button>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

declare global { interface Window { FB?: any; fbAsyncInit?: () => void } }

/** Load Meta's JS SDK once and initialise it for our app. */
function loadFacebookSdk(appId: string, version: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if (window.FB) { window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version }); return resolve(window.FB); }
    window.fbAsyncInit = () => { window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version }); resolve(window.FB); };
    const sc = document.createElement('script');
    sc.src = 'https://connect.facebook.net/en_US/sdk.js'; sc.async = true; sc.defer = true; sc.crossOrigin = 'anonymous';
    sc.onerror = () => reject(new Error('Could not load the Facebook SDK (blocked by the browser or network?)'));
    document.body.appendChild(sc);
  });
}

/**
 * "Connect with Facebook" — Meta's Embedded Signup with our Facebook Login for Business configuration.
 * The admin logs in to Meta, picks (or creates) the WhatsApp Business Account and number; Meta posts
 * the WABA + number ids to this page and returns a one-time code, which the server swaps for the
 * business token. No ids or tokens are copied by hand.
 */
function EmbeddedSignup({ cfg, isAdmin }: { cfg: WaConfig; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const session = useRef<{ waba_id?: string; phone_number_id?: string }>({});

  useEffect(() => {
    // Meta reports what was picked in the popup by postMessage from facebook.com.
    const onMsg = (ev: MessageEvent) => {
      if (!/(^|\.)facebook\.com$/.test(new URL(ev.origin).hostname)) return;
      try {
        const d = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
        if (d?.type === 'WA_EMBEDDED_SIGNUP' && String(d.event ?? '').startsWith('FINISH')) session.current = d.data ?? {};
      } catch { /* not ours */ }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const connect = async () => {
    if (!cfg.appId || !cfg.configId) { toast.error('Save the Meta App ID and login configuration ID first.'); return; }
    if (!cfg.hasAppSecret) { toast.error('Save the App secret first — it is needed to finish the connection.'); return; }
    setBusy(true); setResult(null); session.current = {};
    try {
      const FB = await loadFacebookSdk(cfg.appId, cfg.apiVersion || 'v21.0');
      const code: string | null = await new Promise((resolve) => FB.login((resp: any) => resolve(resp?.authResponse?.code ?? null), {
        config_id: cfg.configId, response_type: 'code', override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      }));
      if (!code) { toast.error('Facebook login was closed before finishing.'); return; }
      await new Promise((r) => setTimeout(r, 500));   // the ids message can land just after the login callback
      const r = (await api.post('/whatsapp/embedded-signup', { code, wabaId: session.current.waba_id, phoneNumberId: session.current.phone_number_id, pin: pin || undefined })).data;
      setResult(r);
      qc.invalidateQueries({ queryKey: ['whatsapp'] });
      toast.success(`WhatsApp connected: ${r.displayPhoneNumber ?? r.phoneNumberId}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? e?.message ?? 'Could not connect');
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2" data-testid="wa-embedded">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="text-sm font-medium text-slate-800">Connect with Facebook</div>
          <div className="text-[11px] text-slate-500">Log in to Meta, choose the WhatsApp Business Account and number — the token, account ID and number ID are filled in for you. Uses login configuration <code>{cfg.configId || '—'}</code> of app <code>{cfg.appId || '—'}</code>.</div>
        </div>
        {isAdmin && (
          <>
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit PIN (new number)" className="input w-48 text-xs" title="Two-step verification PIN — needed to register a number that has not sent messages through the Cloud API before" data-testid="wa-pin" />
            <button onClick={connect} disabled={busy} data-testid="wa-connect-fb"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2 disabled:opacity-60" style={{ background: '#1877F2' }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Facebook size={14} />} Connect with Facebook
            </button>
          </>
        )}
      </div>
      {cfg.phoneNumberId && cfg.connectedAt && (
        <div className="text-xs text-emerald-700 flex items-center gap-1" data-testid="wa-connected">
          <CheckCircle2 size={13} /> Connected{cfg.displayPhoneNumber ? `: ${cfg.displayPhoneNumber}` : ''}{cfg.verifiedName ? ` (${cfg.verifiedName})` : ''} · WABA {cfg.wabaId} · {cfg.connectedVia === 'EMBEDDED_SIGNUP' ? 'via Facebook' : 'entered by hand'} · {new Date(cfg.connectedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
        </div>
      )}
      {result && (
        <div className="text-xs text-slate-600 space-y-0.5">
          <div>Webhooks subscribed: <b>{result.subscribed ? 'yes' : 'no'}</b>{result.registered !== null && <> · Number registered: <b>{result.registered ? 'yes' : 'no'}</b></>} · Templates synced: <b>{result.templates ?? '—'}</b></div>
          {result.warnings?.map((w: string) => <div key={w} className="text-amber-700">⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function TestSend() {
  const { data: tpl } = useQuery({ queryKey: ['whatsapp', 'templates'], queryFn: async () => (await api.get<{ items: WaTemplate[] }>('/whatsapp/templates')).data });
  const approved = (tpl?.items ?? []).filter((t) => t.status === 'APPROVED');
  const [mobile, setMobile] = useState('');
  const [pick, setPick] = useState('');
  const send = useMutation({
    mutationFn: async () => { const [template, language] = pick.split('|'); return (await api.post('/whatsapp/test', { mobile, template, language })).data; },
    onSuccess: () => toast.success(`Test WhatsApp sent to ${mobile}`),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Test failed'),
  });
  return (
    <div className="border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
      <Field label="Send a test WhatsApp to"><input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" className="input w-44" /></Field>
      <Field label="Template">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="input w-64">
          <option value="">— approved template —</option>
          {approved.map((t) => <option key={key(t.name, t.language)} value={key(t.name, t.language)}>{t.name} ({t.language})</option>)}
        </select>
      </Field>
      <button onClick={() => send.mutate()} disabled={send.isPending || !pick || mobile.replace(/\D/g, '').length < 10}
        className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2">
        {send.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
      </button>
      <p className="text-[11px] text-slate-500 flex-1 min-w-[200px]">Filled with sample values. Not charged to anyone.</p>
    </div>
  );
}

function TemplatesTab({ items, syncedAt, isAdmin }: { items: WaTemplate[]; syncedAt: string | null; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const sync = useMutation({
    mutationFn: async () => (await api.post('/whatsapp/templates/sync')).data,
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ['whatsapp', 'templates'] }); toast.success(`Synced ${r.count} template(s) from Meta`); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Sync failed'),
  });
  const counts = useMemo(() => items.reduce<Record<string, number>>((a, t) => { a[t.status] = (a[t.status] ?? 0) + 1; return a; }, {}), [items]);
  const shown = items.filter((t) => !filter || t.status === filter);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs text-slate-500">Last synced: {syncedAt ? new Date(syncedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'never'}</div>
        <div className="flex gap-1">
          {['', ...Object.keys(counts)].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`text-[11px] border rounded-full px-2 py-0.5 ${filter === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
              {s || 'All'} {s ? counts[s] : items.length}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button onClick={() => sync.mutate()} disabled={sync.isPending} data-testid="wa-sync"
            className="ml-auto px-3 py-1.5 bg-primary text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
            {sync.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync from Meta
          </button>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr><th className="text-left px-3 py-2">Template</th><th className="text-left px-3 py-2">Language</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Message</th></tr>
          </thead>
          <tbody>
            {!shown.length ? <tr><td colSpan={5} className="text-center py-6 text-slate-400">{items.length ? 'None with this status.' : 'No templates yet — Sync from Meta.'}</td></tr> : shown.map((t) => (
              <tr key={key(t.name, t.language)} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2 text-xs">{t.language}</td>
                <td className="px-3 py-2 text-xs">{t.category}</td>
                <td className="px-3 py-2"><span className={`text-[11px] border rounded-full px-2 py-0.5 ${STATUS_CHIP[t.status] ?? STATUS_CHIP.PAUSED}`}>{t.status}</span></td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[480px] whitespace-pre-wrap">
                  {t.headerText && <div className="font-semibold">{t.headerText}</div>}
                  {t.headerFormat && t.headerFormat !== 'TEXT' && <div className="text-amber-700">[{t.headerFormat} header — cannot be sent automatically]</div>}
                  {t.bodyText}
                  {t.buttons.length > 0 && <div className="text-slate-400 mt-1">Buttons: {t.buttons.map((b) => b.text).join(' · ')}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventsTab({ cfg, items, isAdmin }: { cfg: WaConfig; items: WaTemplate[]; isAdmin: boolean }) {
  const qc = useQueryClient();
  const approved = items.filter((t) => t.status === 'APPROVED');
  const [events, setEvents] = useState(cfg.events);
  const [params, setParams] = useState<Record<string, ParamMap>>(cfg.templateParams ?? {});
  useEffect(() => { setEvents(cfg.events); setParams(cfg.templateParams ?? {}); }, [cfg]);
  const save = useMutation({
    mutationFn: async () => (await api.put('/whatsapp/config', { events, templateParams: params })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['whatsapp', 'config'] }); toast.success('Event templates saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });
  const setMap = (k: string, part: 'header' | 'body', i: number, field: string) =>
    setParams((p) => { const m = { ...(p[k] ?? {}) }; const arr = [...(m[part] ?? [])]; arr[i] = field; m[part] = arr; return { ...p, [k]: m }; });
  const setBtn = (k: string, idx: string, i: number, field: string) =>
    setParams((p) => { const m = { ...(p[k] ?? {}) }; const b = { ...(m.buttons ?? {}) }; const arr = [...(b[idx] ?? [])]; arr[i] = field; b[idx] = arr; m.buttons = b; return { ...p, [k]: m }; });
  const FieldSelect = ({ value, onChange }: { value?: string; onChange: (v: string) => void }) => (
    <select disabled={!isAdmin} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="input py-1 text-xs w-52">
      <option value="">— not filled ("-") —</option>
      {cfg.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
    </select>
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">The default template for each event. A merchant can be given its own template on its WhatsApp tab. Only Meta-APPROVED templates can be picked; map each <code>{'{{n}}'}</code> to a shipment field. B2C and B2B have their own template for every event.</p>
      {events.map((ev, ei) => (
        <div key={ev.event} className="border border-slate-200 rounded-lg p-3 space-y-3" data-testid={`wa-event-${ev.event}`}>
          <div className="font-medium text-sm">{ev.label}{ev.event === 'INVOICE' && <span className="ml-2 text-[11px] font-normal text-slate-500">sent to the merchant's mobile when an invoice is raised · not charged</span>}</div>
          {(['B2C', 'B2B'] as const).map((type) => {
            const ref = ev[type] ?? {};
            const k = key(ref.template, ref.language);
            const t = items.find((x) => x.name === ref.template && x.language === ref.language);
            return (
              <div key={type} className="space-y-2" data-testid={`wa-event-${ev.event}-${type}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`text-[11px] font-semibold rounded px-2 py-0.5 w-12 text-center ${type === 'B2C' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}`}>{type}</span>
                  <select disabled={!isAdmin} value={k === '|' ? '' : k} className="input py-1.5 text-sm w-72"
                    onChange={(e) => { const [template, language] = e.target.value.split('|'); setEvents((p) => p.map((x, i) => (i === ei ? { ...x, [type]: template ? { template, language } : {} } : x))); }}>
                    <option value="">— no template (not sent) —</option>
                    {approved.map((x) => <option key={key(x.name, x.language)} value={key(x.name, x.language)}>{x.name} ({x.language})</option>)}
                    {t && t.status !== 'APPROVED' && <option value={k}>{t.name} ({t.language}) — {t.status}</option>}
                  </select>
                  {ref.template && !t && <span className="text-xs text-rose-600">not in the synced list — sync templates</span>}
                  {t && t.status !== 'APPROVED' && <span className="text-xs text-amber-700">{t.status} — will not send until approved</span>}
                </div>
                {t && (
                  <div className="pl-16 space-y-1">
                    <div className="text-xs text-slate-500 whitespace-pre-wrap bg-slate-50 rounded p-2">{t.headerText ? `${t.headerText}\n` : ''}{t.bodyText}</div>
                    {t.headerVars.map((v, i) => (<div key={`h${v}`} className="flex items-center gap-2 text-xs"><span className="w-28 text-slate-500">Header {`{{${v}}}`}</span><FieldSelect value={params[k]?.header?.[i]} onChange={(f) => setMap(k, 'header', i, f)} /></div>))}
                    {t.bodyVars.map((v, i) => (<div key={`b${v}`} className="flex items-center gap-2 text-xs"><span className="w-28 text-slate-500">Body {`{{${v}}}`}</span><FieldSelect value={params[k]?.body?.[i]} onChange={(f) => setMap(k, 'body', i, f)} /></div>))}
                    {Object.entries(t.buttonVars).map(([idx, vars]) => vars.map((v, i) => (
                      <div key={`u${idx}${v}`} className="flex items-center gap-2 text-xs"><span className="w-28 text-slate-500">Button {Number(idx) + 1} link {`{{${v}}}`}</span><FieldSelect value={params[k]?.buttons?.[idx]?.[i]} onChange={(f) => setBtn(k, idx, i, f)} /></div>
                    )))}
                    <div className="text-[10px] text-slate-400">The field mapping belongs to the template, so a template used for both B2C and B2B is filled the same way in both.</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="wa-save-events" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save event templates
          </button>
        </div>
      )}
      <p className="text-[11px] text-slate-400">Booking, Delivered and Delivery Failed go out within a minute of the status; Feedback goes {cfg.feedbackDelayHours} h after delivery; Invoice within 5 minutes of the invoice being raised (to the merchant, not charged). Each shipment gets each message once (a failed attempt: once per day).</p>
    </div>
  );
}

function InvoicesTab() {
  const qc = useQueryClient();
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 15);
  const [month, setMonth] = useState(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
  const [gstin, setGstin] = useState('');
  const [taxType, setTaxType] = useState<'CGST_SGST' | 'IGST'>('CGST_SGST');
  const { data: companies } = useQuery({
    queryKey: ['settings', 'invoice_companies'],
    queryFn: async () => { try { return (await api.get<{ items?: any[] }>('/settings/invoice_companies')).data ?? null; } catch { return null; } },
  });
  const list: any[] = Array.isArray(companies) ? companies : (companies?.items ?? []);
  const { data: preview, isFetching } = useQuery({
    queryKey: ['whatsapp', 'invoices', month],
    queryFn: async () => (await api.get('/whatsapp/invoices/preview', { params: { month } })).data as any,
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
  const gen = useMutation({
    mutationFn: async () => (await api.post('/whatsapp/invoices/generate', { month, gstin, taxType })).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['whatsapp'] }); qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.success(`${r.created.length} WhatsApp invoice(s) raised: ${r.created.map((c: any) => c.invoiceNo).join(', ')}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not raise invoices'),
  });
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Postpaid merchants are billed for WhatsApp separately from freight: one invoice per merchant per service type per month (<code>GL/WA/B2C/…</code>, <code>GL/WA/B2B/…</code>). They appear under Invoices and in the Client Ledger. Failed messages are never billed; a message is billed once.</p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Month"><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input" /></Field>
        <Field label="Raise from GSTIN">
          <select value={gstin} onChange={(e) => setGstin(e.target.value)} className="input w-72">
            <option value="">— choose —</option>
            {list.map((c: any) => <option key={c.company_gstin} value={c.company_gstin}>{c.company_gstin} · {c.company_name}</option>)}
          </select>
        </Field>
        <Field label="Tax"><select value={taxType} onChange={(e) => setTaxType(e.target.value as any)} className="input w-40"><option value="CGST_SGST">CGST + SGST</option><option value="IGST">IGST</option></select></Field>
        <button onClick={() => { if (confirm(`Raise ${preview?.rows?.length ?? 0} WhatsApp invoice(s) for ${month}?`)) gen.mutate(); }}
          disabled={gen.isPending || !gstin || !preview?.rows?.length} data-testid="wa-generate"
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
          {gen.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Raise invoices
        </button>
      </div>
      {preview && (
        <div className="grid grid-cols-3 gap-3 max-w-xl">
          <Card label="Invoices to raise" value={String(preview.totals.invoices)} />
          <Card label="Messages" value={String(preview.totals.messages)} />
          <Card label="Amount before GST" value={`₹${Number(preview.totals.subtotal).toFixed(2)}`} />
        </div>
      )}
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="text-left px-3 py-2">Merchant</th><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Messages</th><th className="text-right px-3 py-2">Subtotal</th><th className="text-right px-3 py-2">GST</th><th className="text-right px-3 py-2">Total</th></tr></thead>
          <tbody>
            {isFetching && !preview ? <tr><td colSpan={6} className="py-6 text-center"><Loader2 size={14} className="animate-spin inline" /></td></tr>
              : !preview?.rows?.length ? <tr><td colSpan={6} className="py-6 text-center text-slate-400">Nothing to invoice for {month}.</td></tr>
              : preview.rows.map((r: any) => (
                <tr key={`${r.merchantId}-${r.orderType}`} className="border-t border-slate-100">
                  <td className="px-3 py-2">{r.merchant}</td><td className="px-3 py-2">{r.orderType}</td>
                  <td className="px-3 py-2 text-right">{r.messages}</td><td className="px-3 py-2 text-right">₹{r.subtotal.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">₹{r.gst.toFixed(2)}</td><td className="px-3 py-2 text-right font-medium">₹{r.total}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
